import { AxiosError } from 'axios';

export type SaveErrorContext = {
  nameForKey?: (key: string) => string | undefined;
  documentName: string;
  respondents: string;
};

type ErrorPayload = unknown;

/** DRF nests errors to mirror the payload, so the path to a leaf names the section or question. */
const collectLeaves = (payload: ErrorPayload, path: string[] = []): Array<{ path: string[]; message: string }> => {
  if (typeof payload === 'string') return [{ path, message: payload }];

  if (Array.isArray(payload)) {
    return payload.flatMap((entry, index) => collectLeaves(entry, [...path, String(index)]));
  }

  if (payload && typeof payload === 'object') {
    return Object.entries(payload as Record<string, ErrorPayload>).flatMap(([key, value]) =>
      collectLeaves(value, [...path, key])
    );
  }

  return [];
};

const rewordings = ({
  documentName,
  respondents,
}: SaveErrorContext): Array<{ match: RegExp; message: (subject: string) => string }> => [
  {
    match: /must refer to a following section/,
    message: (subject) => `${subject} routes backwards. A rule can only send ${respondents} to a later section.`,
  },
  {
    match: /must refer to a section/,
    message: (subject) => `${subject} routes to a section that no longer exists.`,
  },
  {
    match: /must refer to a question/,
    message: (subject) => `${subject} branches on a question that is no longer in that section.`,
  },
  {
    match: /must not refer to a multi-record lookup/,
    message: (subject) =>
      `${subject} branches on a lookup question that can hold several records, which cannot be matched.`,
  },
  {
    match: /must be a valid answer/,
    message: (subject) => `${subject} branches on an answer that no longer exists. Fix the rule or restore the answer.`,
  },
  {
    match: /question does not allow any answers/,
    message: (subject) => `${subject} is a dropdown with no answer options.`,
  },
  {
    match: /Repeating group questions are not available/,
    message: () =>
      `This ${documentName} contains a repeating group question, which this organisation can no longer use. Delete the section holding it to save.`,
  },
  {
    match: /Unknown repeating group variant/,
    message: () => `This ${documentName} contains a repeating group question of a type Vatix no longer offers.`,
  },
  {
    match: /may not be blank/,
    message: (subject) => `${subject} is missing text that is required.`,
  },
  {
    match: /an action is required|a condition is required/,
    message: (subject) => `${subject} has a rule with nothing chosen for it to do.`,
  },
];

export const readableSaveErrors = (error: AxiosError, context: SaveErrorContext): string[] => {
  const payload = error.response?.data;
  if (!payload) return [];

  // A DRF error is an object or a list. A bare string means something else answered - a proxy, or
  // Django's own error page - and rendering it drops a whole HTML document into the banner.
  if (typeof payload !== 'object') {
    return [`The server could not publish this ${context.documentName} (${error.response?.status ?? 'no response'}).`];
  }

  const contextRewordings = rewordings(context);
  const messages = collectLeaves(payload).map(({ path, message }) => {
    const subjectName = [...path]
      .reverse()
      .map((segment) => context.nameForKey?.(segment))
      .find(Boolean);
    const subject = subjectName ? `“${subjectName}”` : `This ${context.documentName}`;

    const rewording = contextRewordings.find((candidate) => candidate.match.test(message));
    return rewording ? rewording.message(subject) : `${subject}: ${message}`;
  });

  return [...new Set(messages)];
};
